// Implements: F-UIX-024, F-UIX-007
/**
 * The applied design profile as a chip in the Lienzo editor's topbar (UX-05): it names the profile
 * that styles the mockup and opens `AppliedProfileIndicatorContainer` (where it came from, the
 * newer version, the explicit re-pin) in a popover. It reads the same query as the indicator, so
 * opening it costs no request. While the design is unknown (loading, refused) there is no chip:
 * the indicator's own states are inside the popover, and a chip with nothing to name says nothing.
 */

import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { Icon } from "../../../shared/ui/atoms/Icon";
import { ChipPopover } from "../../../shared/ui/molecules/ChipPopover";
import { useMockupDesign } from "../hooks/useMockupDesign";
import { AppliedProfileIndicatorContainer } from "./AppliedProfileIndicatorContainer";
import styles from "./AppliedProfileChipContainer.module.css";

export interface AppliedProfileChipContainerProps {
  readonly mockupId: string;
}

export function AppliedProfileChipContainer({ mockupId }: AppliedProfileChipContainerProps): ReactElement | null {
  const { t } = useTranslation();
  const design = useMockupDesign(mockupId);
  if (design.data === undefined) return null;
  return (
    <ChipPopover
      label={t("editor.profile.chip.label", { name: design.data.profile_name })}
      title={t("design.indicator.title")}
      description={t("editor.profile.chip")}
      leading={<Icon name="palette" size="sm" />}
      width="md"
    >
      {/* Owner round 3: what a design profile is and when it was applied, before the details. */}
      <p className={styles.explain}>{t("design.profile.what")}</p>
      <p className={styles.explain}>{t("design.indicator.appliedWhen")}</p>
      <AppliedProfileIndicatorContainer mockupId={mockupId} />
    </ChipPopover>
  );
}
